import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Chrome, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(friendlyError(err.code));
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try {
      await loginWithGoogle();
      navigate("/");
    } catch (err) {
      setError(friendlyError(err.code));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="Stock Correlations" className="h-20 w-auto" />
        </div>

        <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-8">
          <h1 className="font-heading text-2xl font-black text-white mb-2">Welcome back</h1>
          <p className="text-[#A1A1AA] text-sm mb-6">Sign in to your Stock Correlations account</p>

          {error && (
            <div className="bg-[#F43F5E]/10 border border-[#F43F5E]/20 text-[#F43F5E] text-sm p-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required
                  className="w-full bg-[#09090B] border border-[#27272A] text-white text-sm pl-10 pr-4 py-2.5 rounded-md focus:outline-none focus:border-[#38BDF8] placeholder-[#52525B]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input
                  type={showPw ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required
                  className="w-full bg-[#09090B] border border-[#27272A] text-white text-sm pl-10 pr-10 py-2.5 rounded-md focus:outline-none focus:border-[#38BDF8] placeholder-[#52525B]"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-black font-bold h-11">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#27272A]" /></div>
            <div className="relative flex justify-center"><span className="bg-[#18181B] px-3 text-xs text-[#52525B]">or continue with</span></div>
          </div>

          <Button onClick={handleGoogle} disabled={loading} variant="outline"
            className="w-full border-[#27272A] text-white hover:bg-[#27272A] h-11">
            <Chrome className="w-4 h-4 mr-2" /> Google
          </Button>

          <p className="text-center text-sm text-[#52525B] mt-6">
            No account?{" "}
            <Link to="/signup" className="text-[#38BDF8] hover:text-white transition-colors">Create one free</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function friendlyError(code) {
  const map = {
    "auth/user-not-found": "No account found with that email.",
    "auth/wrong-password": "Incorrect password.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
    "auth/invalid-credential": "Incorrect email or password.",
  };
  return map[code] || "Sign in failed. Please try again.";
}
